/**
 * Free-play endless: scripted open, then rotating adversary strategies.
 * Score = survival time (elapsed). Only integrity 0 ends the run.
 */
import type { ScenarioDef } from "./scenario";

/**
 * Soft teaching open (~90s), then endless strategies take over.
 * Early beats match campaign teaching density more than Take Every Thought.
 */
export const ENDLESS_FREE_PLAY: ScenarioDef = {
  id: "endless-free",
  name: "Hold the Line",
  mode: "endless",
  duration: 0,
  handSize: 5,
  maxIntegrity: 90,
  disableConfusion: true,
  spawns: [
    { at: 3, lieId: "whisper", label: "First whispers" },
    { at: 10, lieId: "whisper" },
    { at: 18, lieId: "heavy-gloom", label: "Gloom settles" },
    { at: 26, lieId: "whisper", layer: "heart", label: "Heart bypass" },
    { at: 34, lieId: "whisper" },
    { at: 42, lieId: "alone", label: "Isolation" },
    { at: 50, lieId: "wildfire", label: "Spark" },
    {
      at: 54,
      lieId: "nagging-question",
      layer: "mind",
      label: "A nagging question",
      /** Teach: core only until wrong Word tangles it */
      doubtScaffold: "core",
    },
    { at: 58, lieId: "whisper", layer: "heart" },
    { at: 66, lieId: "anchor-weight", layer: "heart", label: "Weight on the heart" },
    { at: 70, lieId: "empty-words", label: "Do they count?" },
    { at: 74, lieId: "heavy-gloom" },
    { at: 82, lieId: "whisper", layer: "will", label: "Will under fire" },
    { at: 90, lieId: "alone", layer: "will", label: "Script ends — protocols begin" },
  ],
  escalation: {
    leadIn: 4,
    strategies: [
      {
        id: "settled",
        label: "Protocol: Settled",
        duration: 44,
        baseInterval: 18,
        pool: [
          { lieId: "settled", weight: 4 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "whisper-net",
        label: "Protocol: Whisper Net",
        duration: 28,
        baseInterval: 7.5,
        pool: [
          { lieId: "whisper", weight: 3 },
          { lieId: "whisper", layer: "heart", weight: 1 },
          { lieId: "nagging-question", weight: 1 },
        ],
      },
      {
        id: "nagging-doubt",
        label: "Protocol: Nagging Doubt",
        duration: 26,
        baseInterval: 9,
        pool: [
          { lieId: "nagging-question", weight: 2, doubtScaffold: "full" },
          {
            lieId: "nagging-question",
            layer: "heart",
            weight: 1,
            doubtScaffold: "half",
          },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "empty-words",
        label: "Protocol: Empty Words",
        duration: 28,
        baseInterval: 7.6,
        pool: [
          { lieId: "empty-words", weight: 3, huntPrayers: true },
          {
            lieId: "empty-words",
            layer: "heart",
            weight: 2,
            huntPrayers: true,
          },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "heart-bypass",
        label: "Protocol: Heart Bypass",
        duration: 30,
        baseInterval: 7,
        pool: [
          { lieId: "whisper", layer: "heart", weight: 2 },
          { lieId: "anchor-weight", layer: "heart", weight: 2 },
          { lieId: "wildfire", layer: "heart", weight: 1 },
        ],
      },
      {
        id: "isolation",
        label: "Protocol: Isolation",
        duration: 28,
        baseInterval: 8,
        pool: [
          { lieId: "alone", weight: 2 },
          { lieId: "alone", layer: "will", weight: 2 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "gloom-guard",
        label: "Protocol: Gloom Guard",
        duration: 30,
        baseInterval: 7.2,
        pool: [
          { lieId: "heavy-gloom", weight: 2 },
          { lieId: "heavy-gloom", layer: "heart", weight: 1 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "will-siege",
        label: "Protocol: Will Siege",
        duration: 32,
        baseInterval: 6.5,
        pool: [
          { lieId: "whisper", layer: "will", weight: 2 },
          { lieId: "alone", layer: "will", weight: 2 },
          { lieId: "anchor-weight", layer: "will", weight: 1 },
          { lieId: "wildfire", layer: "will", weight: 1 },
        ],
      },
      {
        id: "wildfire-spread",
        label: "Protocol: Wildfire",
        duration: 28,
        baseInterval: 6.2,
        pool: [
          { lieId: "wildfire", weight: 2 },
          { lieId: "wildfire", layer: "heart", weight: 2 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "full-assault",
        label: "Protocol: Full Assault",
        duration: 36,
        baseInterval: 5.5,
        pool: [
          { lieId: "whisper", weight: 1 },
          { lieId: "heavy-gloom", weight: 1 },
          { lieId: "alone", weight: 1 },
          { lieId: "empty-words", weight: 1, huntPrayers: true },
          { lieId: "wildfire", layer: "heart", weight: 1 },
          { lieId: "anchor-weight", layer: "will", weight: 1 },
          { lieId: "whisper", layer: "will", weight: 1 },
        ],
      },
    ],
  },
};

/**
 * Muster climb: no soft scripted open — protocols begin almost immediately.
 * Slightly tighter integrity and denser intervals than open free play.
 * Tune later from real runs.
 */
export const MUSTER_FREE_PLAY: ScenarioDef = {
  id: "endless-muster",
  name: "Muster",
  mode: "endless",
  duration: 0,
  handSize: 5,
  maxIntegrity: 80,
  disableConfusion: true,
  /** Empty script → escalation starts after leadIn only. */
  spawns: [],
  escalation: {
    leadIn: 3,
    strategies: [
      {
        id: "settled",
        label: "Protocol: Settled",
        duration: 42,
        baseInterval: 16,
        pool: [
          { lieId: "settled", weight: 4 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "whisper-net",
        label: "Protocol: Whisper Net",
        duration: 26,
        baseInterval: 6.8,
        pool: [
          { lieId: "whisper", weight: 3 },
          { lieId: "whisper", layer: "heart", weight: 1 },
          { lieId: "nagging-question", weight: 1 },
        ],
      },
      {
        id: "nagging-doubt",
        label: "Protocol: Nagging Doubt",
        duration: 24,
        baseInterval: 8,
        pool: [
          { lieId: "nagging-question", weight: 2, doubtScaffold: "full" },
          {
            lieId: "nagging-question",
            layer: "heart",
            weight: 1,
            doubtScaffold: "half",
          },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "empty-words",
        label: "Protocol: Empty Words",
        duration: 26,
        baseInterval: 6.8,
        pool: [
          { lieId: "empty-words", weight: 3, huntPrayers: true },
          {
            lieId: "empty-words",
            layer: "heart",
            weight: 2,
            huntPrayers: true,
          },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "heart-bypass",
        label: "Protocol: Heart Bypass",
        duration: 28,
        baseInterval: 6.4,
        pool: [
          { lieId: "whisper", layer: "heart", weight: 2 },
          { lieId: "anchor-weight", layer: "heart", weight: 2 },
          { lieId: "wildfire", layer: "heart", weight: 1 },
        ],
      },
      {
        id: "isolation",
        label: "Protocol: Isolation",
        duration: 26,
        baseInterval: 7.2,
        pool: [
          { lieId: "alone", weight: 2 },
          { lieId: "alone", layer: "will", weight: 2 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "gloom-guard",
        label: "Protocol: Gloom Guard",
        duration: 28,
        baseInterval: 6.5,
        pool: [
          { lieId: "heavy-gloom", weight: 2 },
          { lieId: "heavy-gloom", layer: "heart", weight: 1 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "will-siege",
        label: "Protocol: Will Siege",
        duration: 30,
        baseInterval: 5.8,
        pool: [
          { lieId: "whisper", layer: "will", weight: 2 },
          { lieId: "alone", layer: "will", weight: 2 },
          { lieId: "anchor-weight", layer: "will", weight: 1 },
          { lieId: "wildfire", layer: "will", weight: 1 },
        ],
      },
      {
        id: "wildfire-spread",
        label: "Protocol: Wildfire",
        duration: 26,
        baseInterval: 5.5,
        pool: [
          { lieId: "wildfire", weight: 2 },
          { lieId: "wildfire", layer: "heart", weight: 2 },
          { lieId: "whisper", weight: 1 },
        ],
      },
      {
        id: "full-assault",
        label: "Protocol: Full Assault",
        duration: 34,
        baseInterval: 4.9,
        pool: [
          { lieId: "whisper", weight: 1 },
          { lieId: "heavy-gloom", weight: 1 },
          { lieId: "alone", weight: 1 },
          { lieId: "empty-words", weight: 1, huntPrayers: true },
          { lieId: "wildfire", layer: "heart", weight: 1 },
          { lieId: "anchor-weight", layer: "will", weight: 1 },
          { lieId: "whisper", layer: "will", weight: 1 },
        ],
      },
    ],
  },
};
